import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { 
  Employee, 
  ChargeRate,
  FilterConfig, 

  EmployeeFormData,
  ChargeRateFormData,
  filterEmployees,
  sortEmployees
  } from './config';
import EmployeeTable from './components/EmployeeTable';
import EmployeeDetailModal from './components/EmployeeDetailModal';
import { notifyReadOnly } from '../../utils/supabaseErrors';
import Dropdown from '../../components/Dropdown';
import { DropdownItem } from '../../components/Dropdown/config';
import TableSkeleton from '../../components/TableSkeleton';
import ListPage, { FilterTrigger, SearchInput, StatusSegments, useNarrowViewport } from '../../components/ListPage';
import {
  EMPLOYEE_TYPE_OPTIONS,
  ROLE_OPTIONS,
  TeamMemberType,
  ManagerialRole
} from './config';

type ActiveSegment = 'all' | 'active' | 'inactive';

const TYPE_ITEMS: DropdownItem[] = [{ id: 'all-types', value: '', label: 'All' }, ...EMPLOYEE_TYPE_OPTIONS];
const ROLE_ITEMS: DropdownItem[] = [{ id: 'all-roles', value: '', label: 'All' }, ...ROLE_OPTIONS];
const RATE_ITEMS: DropdownItem[] = [
  { id: 'all-rates', value: 'all', label: 'All' },
  { id: 'with-rate', value: 'with_rate', label: 'With charge rate' },
  { id: 'without-rate', value: 'without_rate', label: 'Without charge rate' },
];

const EmployeeManagement: React.FC = () => {
  // State management
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter state
  const [filters, setFilters] = useState<FilterConfig>({
    employeeType: null,
    role: null,
    isActive: true, // Default to showing only active employees
    hasChargeRate: null,
    searchText: ''
  });

  // Modal state
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Authentication hooks
  const session = useSession();
  const supabase = useSupabaseClient();

  // Fetch employees from the database
  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('employee_summary')
        .select('*')
        .order('first_name');

      if (error) throw error;

      setEmployees(data || []);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Initialize data on mount
  useEffect(() => {
    if (session?.user) {
      fetchEmployees();
    }
  }, [session, fetchEmployees]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Partial<FilterConfig>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const isNarrow = useNarrowViewport();

  // The Active / Inactive segment counts come from the full list, so they
  // hold still whichever segment is selected.
  const activeCounts = useMemo(() => {
    const active = employees.filter((e) => e.is_active).length;
    return { all: employees.length, active, inactive: employees.length - active };
  }, [employees]);
  const activeSegment: ActiveSegment =
    filters.isActive === null ? 'all' : filters.isActive ? 'active' : 'inactive';
  const handleActiveSegment = useCallback((v: ActiveSegment) => {
    handleFilterChange({ isActive: v === 'all' ? null : v === 'active' });
  }, [handleFilterChange]);

  // Active-only is the default view, so it does not count as a filter.
  const hasActiveFilters =
    filters.employeeType !== null ||
    filters.role !== null ||
    filters.isActive !== true ||
    filters.hasChargeRate !== null ||
    filters.searchText.length > 0;
  const handleClearFilters = useCallback(() => {
    setFilters({ employeeType: null, role: null, isActive: true, hasChargeRate: null, searchText: '' });
  }, []);



  // Modal handlers
  const handleRowClick = useCallback((employee: Employee) => {
    setSelectedEmployee(employee);
    setIsDetailModalOpen(true);
  }, []);

  const handleDetailModalClose = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedEmployee(null);
  }, []);

  // Refresh the list AND the employee the modal is showing, without the
  // page's loading state -- selectedEmployee is separate state, so refetching
  // the list alone would leave the open modal displaying what it had before.
  const refreshEmployees = useCallback(async () => {
    const { data, error } = await supabase
      .from('employee_summary')
      .select('*')
      .order('first_name');

    if (error) {
      console.error('Error refreshing employees:', error);
      return;
    }

    setEmployees(data || []);
    setSelectedEmployee(prev =>
      prev ? (data || []).find((e: Employee) => e.id === prev.id) || prev : prev
    );
  }, [supabase]);

  // Handle updating basic employee info
  const handleUpdateBasicInfo = useCallback(async (employeeId: string, formData: EmployeeFormData): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('team_members')
        .update({
          first_name: formData.first_name,
          last_name: formData.last_name,
          type: formData.type,
          role: formData.role,
          email: formData.email || null,
          position_title: formData.position_title || null,
          registration_number: formData.registration_number || null,
          is_cpeng: formData.is_cpeng,
          signature_scale: formData.signature_scale,
          signature_offset_x_mm: formData.signature_offset_x_mm,
          signature_offset_y_mm: formData.signature_offset_y_mm,
          standard_hours_per_day: Number(formData.standard_hours_per_day),
          default_start_time: formData.default_start_time,
          lunch_start_time: formData.lunch_start_time,
          lunch_minutes: Number(formData.lunch_minutes)
        })
        .eq('id', employeeId);

      if (error) throw error;

      // The modal stays open after a save, so the employee it is showing has
      // to be refreshed with the list.
      await refreshEmployees();
      return true;
    } catch (error) {
      console.error('Error updating employee:', error);
      notifyReadOnly(error);
      return false;
    }
  }, [supabase, refreshEmployees]);

  // Handle deactivating employee
  const handleDeactivateEmployee = useCallback(async (employeeId: string) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .update({ is_active: false })
        .eq('id', employeeId);

      if (error) throw error;

      // Refresh employee list
      await fetchEmployees();
    } catch (err) {
      console.error('Error deactivating employee:', err);
      notifyReadOnly(err);
      setError('Failed to deactivate employee');
    }
  }, [supabase, fetchEmployees]);

  // Handle reactivating employee
  const handleReactivateEmployee = useCallback(async (employeeId: string) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .update({ is_active: true })
        .eq('id', employeeId);

      if (error) throw error;

      // Refresh employee list
      await fetchEmployees();
    } catch (err) {
      console.error('Error reactivating employee:', err);
      notifyReadOnly(err);
      setError('Failed to reactivate employee');
    }
  }, [supabase, fetchEmployees]);

  // Handle adding charge rate
  const handleAddChargeRate = useCallback(async (employeeId: string, formData: ChargeRateFormData) => {
    try {
      // End the current rate if it exists
      if (formData.end_date) {
        const { error: updateError } = await supabase
          .from('charge_rates')
          .update({ end_date: formData.end_date })
          .eq('employee_id', employeeId)
          .is('end_date', null);

        if (updateError) throw updateError;
      }

      // Add the new rate
      const { error } = await supabase
        .from('charge_rates')
        .insert([{
          employee_id: employeeId,
          charge_rate: parseFloat(formData.charge_rate),
          effective_date: formData.effective_date,
          end_date: null
        }]);

      if (error) throw error;

      // Refresh employee list
      await fetchEmployees();
      

      
      return true;
    } catch (err) {
      console.error('Error adding charge rate:', err);
      notifyReadOnly(err);
      throw new Error('Failed to add charge rate');
    }
  }, [supabase, fetchEmployees]);

  // Handle getting charge rate history
  const fetchChargeRateHistory = useCallback(async (employeeId: string): Promise<ChargeRate[]> => {
    try {
      const { data, error } = await supabase
        .from('charge_rates')
        .select('*')
        .eq('employee_id', employeeId)
        .order('effective_date', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching charge rate history:', err);
      return [];
    }
  }, [supabase]);



  // Filter and sort employees
  const filteredEmployees = filterEmployees(employees, filters);
  const sortedEmployees = sortEmployees(filteredEmployees, 'first_name', true);

  const initialLoading = loading && employees.length === 0;

  return (
    <ListPage
      title="Employee Profiles"
      className="employee-management"
      loading={initialLoading}
      skeleton={{ segmentCount: 3, filterCount: 3, showAction: false }}
      search={
        <SearchInput
          placeholder="Search by name or email…"
          value={filters.searchText}
          onChange={(e) => handleFilterChange({ searchText: e.target.value })}
          onClear={() => handleFilterChange({ searchText: '' })}
          aria-label="Search employees"
        />
      }
      segments={
        <StatusSegments<ActiveSegment>
          aria-label="Employment status"
          size={isNarrow ? 'sm' : 'md'}
          value={activeSegment}
          onChange={handleActiveSegment}
          segments={[
            { value: 'all', label: 'All', count: activeCounts.all },
            { value: 'active', label: 'Active', count: activeCounts.active },
            { value: 'inactive', label: 'Inactive', count: activeCounts.inactive },
          ]}
        />
      }
      filters={
        <>
          <FilterTrigger label="Type" active={filters.employeeType !== null}>
            <Dropdown
              items={TYPE_ITEMS}
              value={filters.employeeType || ''}
              onChange={(v: string) => handleFilterChange({ employeeType: v ? (v as TeamMemberType) : null })}
              placeholder="All"
            />
          </FilterTrigger>
          <FilterTrigger label="Role" active={filters.role !== null}>
            <Dropdown
              items={ROLE_ITEMS}
              value={filters.role || ''}
              onChange={(v: string) => handleFilterChange({ role: v ? (v as ManagerialRole) : null })}
              placeholder="All"
            />
          </FilterTrigger>
          <FilterTrigger label="Charge rate" active={filters.hasChargeRate !== null}>
            <Dropdown
              items={RATE_ITEMS}
              value={filters.hasChargeRate === null ? 'all' : filters.hasChargeRate ? 'with_rate' : 'without_rate'}
              onChange={(v: string) => handleFilterChange({ hasChargeRate: v === 'all' ? null : v === 'with_rate' })}
              placeholder="All"
            />
          </FilterTrigger>
        </>
      }
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
      resultsText={!initialLoading && `Showing ${filteredEmployees.length} of ${employees.length} employees`}
    >
      {error && (
        <div className="list-page__error">
          {error}{' '}
          <button type="button" className="link-btn" onClick={fetchEmployees}>Try again</button>
        </div>
      )}

      {initialLoading ? (
        <TableSkeleton compact rows={12} columns={6} showHeader />
      ) : sortedEmployees.length === 0 ? (
        <div className="list-page__empty">
          {hasActiveFilters ? 'No employees match these filters.' : 'No employees yet.'}
        </div>
      ) : (
        <EmployeeTable employees={sortedEmployees} onRowClick={handleRowClick} />
      )}

      {/* Employee Detail Modal */}
      <EmployeeDetailModal
        isOpen={isDetailModalOpen}
        employee={selectedEmployee}
        onClose={handleDetailModalClose}
        onSubmitBasicInfo={handleUpdateBasicInfo}
        onAddChargeRate={handleAddChargeRate}
        onFetchHistory={fetchChargeRateHistory}
        onDeactivateEmployee={handleDeactivateEmployee}
        onReactivateEmployee={handleReactivateEmployee}
        onSignatureChanged={refreshEmployees}
      />
    </ListPage>
  );
};

export default EmployeeManagement; 